import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Baby, ShieldAlert, AlertTriangle } from "lucide-react";

export default function StepLavoratriciMadri({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const d = data || {};
  const s1 = dvr.steps?.step1_dati_generali || {};
  const s4 = dvr.steps?.step4_mansioni || [];
  const [note, setNote] = useState(d._note || "");

  const run = async () => {
    setAiLoading(true);
    try {
      const payload = {
        ateco: s1.ateco,
        tipo_attivita: s1.tipo_attivita,
        numero_donne: s1.numero_donne,
        mansioni: s4.map((m) => ({
          nome: m.mansione,
          rischi: (m.rischi || []).map((r) => r.nome).filter(Boolean),
          sostanze: m.sostanze_tipiche || [],
        })),
        note,
      };
      const r = await api.post("/dvr/ai/lavoratrici-madri", payload, { timeout: 120000 });
      onChange({ ...r.data, _note: note });
      toast.success("Valutazione lavoratrici madri completata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="step-lavoratrici-madri">
      <p className="text-sm text-muted-foreground">
        Valutazione ex <b>D.Lgs. 151/2001</b> (Testo unico maternità). L'AI incrocerà mansioni e
        rischi con gli Allegati A/B/C per identificare astensioni anticipate, mansioni alternative e
        sostanze incompatibili. Numero donne in azienda: <b>{s1.numero_donne || 0}</b>.
      </p>
      <Textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note aggiuntive (es. eventuale gravidanza in corso, mansioni particolari…)"
        data-testid="lm-note"
      />
      <Button onClick={run} disabled={aiLoading} className="rounded-full" data-testid="lm-run-ai">
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Baby className="w-4 h-4 mr-2" />}
        Valuta tutela madri
      </Button>

      {d.valutazione_generale && (
        <div className="border border-border rounded-md p-3 bg-muted/20 text-sm">
          <b>Valutazione generale:</b> {d.valutazione_generale}
        </div>
      )}

      {d.mansioni_a_rischio?.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-sm flex items-center gap-1"><ShieldAlert className="w-4 h-4 text-rose-600" /> Mansioni a rischio</h3>
          {d.mansioni_a_rischio.map((m, i) => (
            <div key={`mar-${m.mansione || i}-${i}`} className={`rounded-md p-3 border ${m.obbligo_astensione_anticipata ? "bg-rose-50 border-rose-300" : "bg-amber-50 border-amber-300"}`}>
              <div className="font-semibold">{m.mansione}</div>
              <div className="text-xs mt-0.5">
                Periodo: <b>{m.periodo_astensione}</b> · Astensione anticipata:{" "}
                {m.obbligo_astensione_anticipata ? "SÌ (obbligo)" : "no"}
              </div>
              {m.rischi_incompatibili?.length > 0 && (
                <div className="text-xs mt-1">Rischi: {m.rischi_incompatibili.join(", ")}</div>
              )}
              <div className="text-xs mt-1">{m.motivazione}</div>
              <div className="text-[11px] text-muted-foreground mt-1">Rif: {m.normativa}</div>
            </div>
          ))}
        </div>
      )}

      {d.mansioni_alternative_possibili?.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm">Mansioni alternative proponibili</h3>
          <ul className="text-sm mt-1 space-y-0.5">
            {d.mansioni_alternative_possibili.map((a, i) => (
              <li key={`alt-${a.mansione || i}-${i}`}>
                • <b>{a.mansione}</b>: {a.descrizione}{" "}
                <span className="text-[11px] text-muted-foreground">(idonea per {a.idonea_per})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.sostanze_incompatibili?.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-amber-600" /> Sostanze incompatibili</h3>
          <ul className="text-sm mt-1 space-y-0.5">
            {d.sostanze_incompatibili.map((s, i) => (
              <li key={`si-${s.sostanza || i}-${i}`}>
                • {s.sostanza} ({s.frase_h}) — <i>{s.periodo}</i>{" "}
                <span className="text-[11px] text-muted-foreground">{s.normativa}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.procedure_azienda?.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm">Procedure aziendali richieste</h3>
          <ul className="text-sm mt-1 space-y-0.5">
            {d.procedure_azienda.map((p, i) => <li key={`pa-${i}-${(p || "").slice(0, 20)}`}>• {p}</li>)}
          </ul>
        </div>
      )}

      {d.documenti_da_produrre?.length > 0 && (
        <div className="text-xs text-muted-foreground">Documenti: {d.documenti_da_produrre.join(" · ")}</div>
      )}
      {d.sintesi && <div className="text-sm text-muted-foreground italic">{d.sintesi}</div>}
    </div>
  );
}
